import type { AllReporterVariant } from '../../hooks/useReporter';

import { Editor, Node } from '../../core';
import { HeadingLevel } from '../heading/constants';
import { HEADING_PLUGIN_ID } from '../heading/types';
import { Element } from 'slate';
import { matchHeadingLikePrefix } from '../headingDecoration/matcher';
import { isHeadingLikeParagraph, leadingText } from '../headingDecoration/sectionHelpers';

export const getDuplicateHeadingErrorText = (variant: AllReporterVariant): string => {
  switch (variant) {
    case 'report':
      return 'Cannot sign report with duplicate section headers';
    case 'template':
      return 'Cannot save template with duplicate section headers';
    default:
      return 'Cannot have duplicate section headers';
  }
};

export const normalizeText = (text: string): string => {
  return text
    .trim()
    .toLowerCase()
    .replace(/[^\w\s']|_/g, '')
    .replace(/\s+/g, ' ');
};

export const isH1HeadingNode = (node: Node): boolean => {
  return (
    Element.isElement(node) && node.type === HEADING_PLUGIN_ID && node.level === HeadingLevel.H1
  );
};

export const doesH1HeadingMatchText = (node: Node, normalizedHeadingText: string): boolean => {
  return isH1HeadingNode(node) && normalizeText(Node.string(node)) === normalizedHeadingText;
};

// RP-6009 (M6): the duplicate-header sign gate. Detects duplicate section names among
// H1 heading NODES and — when a `labelSet` is supplied (headings-as-decoration) — also
// among heading-like paragraph labels, keyed the same way so the two representations
// dedupe consistently. Without a label set this is byte-identical to the node-only gate.
const headingDedupeKey = (node: Node, labelSet?: Set<string>): string | null => {
  if (isH1HeadingNode(node)) {
    return normalizeText(Node.string(node));
  }
  if (labelSet != null && isHeadingLikeParagraph(node, labelSet)) {
    // Same leading text run `isHeadingLikeParagraph` matched on, so a label split across
    // leaves (e.g. bold `FINDINGS` + plain `:`) still yields a key.
    const match = matchHeadingLikePrefix(leadingText(node), labelSet);
    if (match != null) return normalizeText(match.label);
  }
  return null;
};

export const hasDuplicateHeadingsInNode = (root: Node, labelSet?: Set<string>): boolean => {
  const seenHeadings = new Set<string>();

  for (const [node] of Array.from(Node.nodes(root))) {
    const headingText = headingDedupeKey(node, labelSet);
    if (headingText == null || headingText === '') {
      continue;
    }

    if (seenHeadings.has(headingText)) {
      return true;
    }

    seenHeadings.add(headingText);
  }

  return false;
};

export const reportHasDuplicateHeadings = (editor: Editor, text?: string): boolean => {
  const normalizedText = text == null ? undefined : normalizeText(text);

  return (
    Array.from(
      Editor.nodes(editor, {
        at: [],
        match: (n) =>
          isH1HeadingNode(n) &&
          (normalizedText == null || doesH1HeadingMatchText(n, normalizedText)) &&
          Array.from(Node.texts(n)).some(
            (textNodeEntry) => (textNodeEntry[0] as any).headingError === true
          ),
      })
    ).length > 0
  );
};
